import React, { useState, useEffect } from "react";
import { VerticalTimeline, VerticalTimelineElement } from "react-vertical-timeline-component";
import "react-vertical-timeline-component/style.min.css";
import { motion } from "framer-motion";
import { Briefcase, ExternalLink } from "lucide-react";

// Define the expected experience type
type Experience = {
  role: string;
  company: string;
  date: string;
  description: string;
  image?: string;
  link?: string;
};

// Framer Motion Animation Variants
const fadeIn = {
  initial: { opacity: 0, y: 50 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeInOut" } },
};

const WorkExperienceTimeline: React.FC = () => {
  const [experience, setExperience] = useState<Experience[]>([]);

  useEffect(() => {
    import("../data/portfolioData.json").then((data) => {
      console.log("Experience Data Loaded:", data.default);
      setExperience(data.default as Experience[]);
    }).catch(error => console.error("Error loading experience data:", error));
  }, []);

  return (
    <section className="relative min-h-screen text-white py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Background Glow Effect */}
      <motion.div
        className="absolute inset-0 w-full h-full pointer-events-none"
        animate={{
          opacity: [0.7, 1, 0.7],
          filter: ["blur(50px)", "blur(70px)", "blur(50px)"],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          repeatType: "mirror",
          ease: "easeInOut",
        }}
        style={{
          background: "radial-gradient(circle, rgba(255,255,255,0.1) 10%, transparent 70%)",
        }}
      />

      <div className="max-w-7xl mx-auto relative">
        {/* Section Header */}
        <motion.div {...fadeIn} className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-white via-gray-400 to-white bg-clip-text text-transparent">
            Work Experience
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            A timeline of my career journey, roles, and contributions in the tech industry.
          </p>
        </motion.div>

        {/* Vertical Timeline */}
        <VerticalTimeline animate={true}>
          {experience.map((job, index) => (
            <VerticalTimelineElement
              key={index}
              date={job.date}
              className="vertical-timeline-element--work"
              contentStyle={{
                background: "transparent",
                boxShadow: "none",
                padding: "0px",
              }}
              contentArrowStyle={{ display: "none" }}
              icon={<Briefcase className="w-8 h-8 text-white" />}
            >
              {/* Work Experience Card Design */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative bg-black/90 backdrop-blur-lg border border-white/20 p-6 rounded-3xl shadow-lg overflow-hidden transition-transform duration-500 hover:scale-105"
              >
                {/* Work Experience Image */}
                {job.image && (
                  <div className="relative w-full h-40 rounded-lg overflow-hidden mb-4">
                    <img
                      src={job.image}
                      alt={job.company}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40"></div>
                  </div>
                )}

                {/* Work Experience Content */}
                <h3 className="text-2xl font-semibold text-white">{job.role}</h3>
                <h4 className="text-lg font-medium text-gray-300">{job.company}</h4>
                <p className="text-gray-300 mt-2">{job.description}</p>

                {/* External Link Button */}
                {job.link && (
                  <a
                    href={job.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 mt-4 text-green-400 hover:text-green-300 transition"
                  >
                    Learn More <ExternalLink size={16} />
                  </a>
                )}
              </motion.div>
            </VerticalTimelineElement>
          ))}
        </VerticalTimeline>
      </div>
    </section>
  );
};

export default WorkExperienceTimeline;


// import React, { useState, useEffect } from "react";
// import { VerticalTimeline, VerticalTimelineElement } from "react-vertical-timeline-component";
// import "react-vertical-timeline-component/style.min.css";
// import { motion } from "framer-motion";
// import { ExternalLink } from "lucide-react";

// // Define the expected project type
// type Project = {
//   title: string;
//   date: string;
//   description: string;
//   link: string;
// };

// // Framer Motion Animation Variants
// const fadeIn = {
//   initial: { opacity: 0, y: 50 },
//   animate: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeInOut" } },
// };

// const ProjectsTimeline: React.FC = () => {
//   const [projects, setProjects] = useState<Project[]>([]);

//   useEffect(() => {
//     import("../data/portfolioData.json").then((data) => {
//       console.log("Projects Data Loaded:", data.default);
//       setProjects(data.default as Project[]);
//     }).catch(error => console.error("Error loading projects:", error));
//   }, []);

//   return (
//     <section className="relative min-h-screen bg-black text-white py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
//       {/* Background Glow Effect */}
//       <motion.div
//         className="absolute inset-0 w-full h-full opacity-50 pointer-events-none"
//         animate={{
//           opacity: [0.7, 1, 0.7],
//           filter: ["blur(50px)", "blur(70px)", "blur(50px)"],
//         }}
//         transition={{
//           duration: 10,
//           repeat: Infinity,
//           repeatType: "mirror",
//           ease: "easeInOut",
//         }}
//         style={{
//           background: "radial-gradient(circle, rgba(255,255,255,0.1) 10%, transparent 70%)",
//         }}
//       />

//       <div className="max-w-7xl mx-auto relative">
//         {/* Section Header */}
//         <motion.div {...fadeIn} className="text-center mb-12">
//           <h2 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-white via-gray-400 to-white bg-clip-text text-transparent">
//             Projects Timeline
//           </h2>
//           <p className="text-xl text-gray-400 max-w-3xl mx-auto">
//             A showcase of cutting-edge AI, web development, and engineering projects.
//           </p>
//         </motion.div>

//         {/* Vertical Timeline */}
//         <VerticalTimeline animate={true}>
//           {projects.map((project, index) => (
//             <VerticalTimelineElement
//               key={index}
//               date={project.date}
//               className="vertical-timeline-element--work"
//               contentStyle={{
//                 background: "transparent",
//                 boxShadow: "none",
//                 padding: "0px",
//               }}
//               contentArrowStyle={{ display: "none" }}
//               icon={<div className="w-10 h-10 bg-white/10 rounded-full" />}
//             >
//               {/* Project Card Design */}
//               <motion.div
//                 initial={{ opacity: 0, y: 20 }}
//                 whileInView={{ opacity: 1, y: 0 }}
//                 transition={{ duration: 0.6, delay: index * 0.1 }}
//                 viewport={{ once: true }}
//                 className="relative group bg-black/90 backdrop-blur-lg border border-gray-800 p-6 rounded-2xl shadow-lg overflow-hidden transition-transform duration-500 hover:scale-105"
//               >
//                 {/* Glowing Gradient Effect */}
//                 <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-[#ff0080] via-[#ff8c00] to-[#ffeb00] blur-2xl" />

//                 {/* Animated Gradient Border on Hover */}
//                 <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 border-2 border-transparent group-hover:border-white/20 rounded-2xl"></div>

//                 {/* Project Content */}
//                 <h3 className="text-2xl font-semibold text-white">{project.title}</h3>
//                 <p className="text-gray-300 mt-2">{project.description}</p>

//                 {/* External Link Button */}
//                 <a
//                   href={project.link}
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   className="flex items-center gap-2 mt-4 text-green-400 hover:text-green-300 transition"
//                 >
//                   View Project <ExternalLink size={16} />
//                 </a>

//                 {/* Floating 3D Blocks Effect */}
//                 <div className="absolute bottom-0 right-0 flex space-x-3 space-y-3 opacity-20">
//                   <div className="w-12 h-12 bg-white/5 rounded-lg transform rotate-45"></div>
//                   <div className="w-8 h-8 bg-white/5 rounded-lg transform rotate-45"></div>
//                   <div className="w-16 h-16 bg-white/5 rounded-lg transform rotate-45"></div>
//                 </div>
//               </motion.div>
//             </VerticalTimelineElement>
//           ))}
//         </VerticalTimeline>
//       </div>
//     </section>
//   );
// };

// export default ProjectsTimeline;



// import React, { useState, useEffect } from "react";
// import { VerticalTimeline, VerticalTimelineElement } from "react-vertical-timeline-component";
// import "react-vertical-timeline-component/style.min.css";
// import { motion } from "framer-motion";
// import { ExternalLink } from "lucide-react";

// // Define the expected project type
// type Project = {
//   title: string;
//   date: string;
//   description: string;
//   link: string;
// };

// // Framer Motion Animation Variants
// const fadeIn = {
//   initial: { opacity: 0, y: 50 },
//   animate: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeInOut" } },
// };

// const ProjectsTimeline: React.FC = () => {
//   const [projects, setProjects] = useState<Project[]>([]);

//   useEffect(() => {
//     import("../data/portfolioData.json").then((data) => {
//       console.log("Projects Data Loaded:", data.default);
//       setProjects(data.default as Project[]);
//     }).catch(error => console.error("Error loading projects:", error));
//   }, []);

//   return (
//     <section className="relative min-h-screen text-white py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
//       {/* Background Glow Effect */}
//       <motion.div
//         className="absolute inset-0 w-full h-full  opacity-50 pointer-events-none"
//         animate={{
//           opacity: [0.7, 1, 0.7],
//           backgroundPosition: ["0% 0%", "0% 100%", "0% 0%"],
//         }}
//         transition={{
//           duration: 8,
//           repeat: Infinity,
//           repeatType: "mirror",
//           ease: "easeInOut",
//         }}
//       />

//       <div className="max-w-7xl mx-auto relative">
//         {/* Section Header */}
//         <motion.div {...fadeIn} className="text-center mb-12">
//           <h2 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-white via-gray-400 to-white bg-clip-text text-transparent">
//             Projects Timeline
//           </h2>
//           <p className="text-xl text-gray-400 max-w-3xl mx-auto">
//             A showcase of cutting-edge AI, web development, and engineering projects.
//           </p>
//         </motion.div>

//         {/* Vertical Timeline */}
//         <VerticalTimeline animate={true}>
//           {projects.map((project, index) => (
//             <VerticalTimelineElement
//               key={index}
//               date={project.date}
//               className="vertical-timeline-element--work"
//               contentStyle={{
//                 background: "transparent",
//                 boxShadow: "none",
//                 padding: "0px",
//               }}
//               contentArrowStyle={{ display: "none" }}
//               icon={<div className="w-10 h-10 bg-white/10 rounded-full" />}
//             >
//               {/* Project Card Design */}
//               <motion.div
//                 initial={{ opacity: 0, y: 20 }}
//                 whileInView={{ opacity: 1, y: 0 }}
//                 transition={{ duration: 0.6, delay: index * 0.1 }}
//                 viewport={{ once: true }}
//                 className="relative bg-black rounded-2xl border border-gray-800 p-6 shadow-lg overflow-hidden transition-transform duration-500 hover:scale-105"
//               >
//                 {/* Glowing Gradient Effect */}
//                 <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80 pointer-events-none"></div>

//                 {/* Project Content */}
//                 <h3 className="text-2xl font-semibold text-white">{project.title}</h3>
//                 <p className="text-gray-300 mt-2">{project.description}</p>

//                 {/* External Link Button */}
//                 <a
//                   href={project.link}
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   className="flex items-center gap-2 mt-4 text-green-400 hover:text-green-300 transition"
//                 >
//                   View Project <ExternalLink size={16} />
//                 </a>

//                 {/* Floating 3D Blocks Effect */}
//                 <div className="absolute bottom-0 right-0 flex space-x-3 space-y-3 opacity-20">
//                   <div className="w-12 h-12 bg-white/5 rounded-lg transform rotate-45"></div>
//                   <div className="w-8 h-8 bg-white/5 rounded-lg transform rotate-45"></div>
//                   <div className="w-16 h-16 bg-white/5 rounded-lg transform rotate-45"></div>
//                 </div>
//               </motion.div>
//             </VerticalTimelineElement>
//           ))}
//         </VerticalTimeline>
//       </div>
//     </section>
//   );
// };

// export default ProjectsTimeline;


// import React, { useState, useEffect } from "react";
// import { VerticalTimeline, VerticalTimelineElement } from "react-vertical-timeline-component";
// import "react-vertical-timeline-component/style.min.css";
// import { ExternalLink, Calendar } from "lucide-react";

// // Define the expected project type
// type Project = {
//   title: string;
//   date: string;
//   description: string;
//   link: string;
// };

// const ProjectsTimeline: React.FC = () => {
//   // Explicitly define the type of state as an array of Project objects
//   const [projects, setProjects] = useState<Project[]>([]);

//   useEffect(() => {
//     import("../data/portfolioData.json").then((data) => {
//       console.log("Projects Data Loaded:", data.default);
//       setProjects(data.default as Project[]);  // Ensure TypeScript understands the type
//     }).catch(error => console.error("Error loading projects:", error));
//   }, []);

//   return (
//     <section className="relative min-h-screen bg-black text-white py-20 px-6">
//       <div className="text-center mb-12">
//         <h2 className="text-4xl font-bold bg-gradient-to-r from-white via-gray-400 to-white bg-clip-text text-transparent">
//           Projects Timeline
//         </h2>
//       </div>

//       {/* Check if projects exist */}
//       {projects.length === 0 ? (
//         <p className="text-gray-400 text-center mt-10">No projects found...</p>
//       ) : (
//         <VerticalTimeline>
//           {projects.map((project, index) => (
//             <VerticalTimelineElement
//               key={index}
//               date={project.date}
//               icon={<Calendar className="text-white" />}
//               className="vertical-timeline-element"
//               contentStyle={{ background: "rgba(0,0,0,0.6)", color: "#fff" }}
//               contentArrowStyle={{ borderRight: "7px solid rgba(255,255,255,0.3)" }}
//             >
//               <h3 className="text-2xl font-semibold">{project.title}</h3>
//               <p className="text-gray-300">{project.description}</p>
//               <a
//                 href={project.link}
//                 target="_blank"
//                 rel="noopener noreferrer"
//                 className="text-green-400 mt-3 flex items-center gap-2 hover:text-green-300 transition"
//               >
//                 View Project <ExternalLink size={16} />
//               </a>
//             </VerticalTimelineElement>
//           ))}
//         </VerticalTimeline>
//       )}
//     </section>
//   );
// };

// export default ProjectsTimeline;


// import React, { useState, useEffect } from "react";
// import { motion } from "framer-motion";
// import { VerticalTimeline, VerticalTimelineElement } from "react-vertical-timeline-component";
// import "react-vertical-timeline-component/style.min.css";
// import { Code, ExternalLink, Calendar } from "lucide-react";

// interface Project {
//   title: string;
//   date: string;
//   description: string;
//   link: string;
// }

// const ProjectsTimeline: React.FC = () => {
//   const [projects, setProjects] = useState<Project[]>([]);

//   useEffect(() => {
//     fetch("/data/portfolioData.json")
//       .then((res) => res.json())
//       .then((data) => setProjects(data));
//   }, []);

//   return (
//     <section className="relative py-32 px-6 lg:px-20 text-white bg-black border-t border-gray-800">
//       {/* Section Title */}
//       <div className="text-center mb-12">
//         <h2 className="text-5xl font-bold text-white">Projects</h2>
//         <p className="text-gray-400 mt-4 text-lg">A collection of my latest work.</p>
//       </div>

//       {/* Timeline */}
//       <VerticalTimeline>
//         {projects.map((project, index) => (
//           <VerticalTimelineElement
//             key={index}
//             className="vertical-timeline-element--work"
//             contentStyle={{ background: "#1a1a1a", color: "#fff", borderRadius: "10px" }}
//             contentArrowStyle={{ borderRight: "7px solid #9333EA" }}
//             date={project.date}
//             iconStyle={{ background: "#9333EA", color: "#fff" }}
//             icon={<Code />}
//           >
//             <motion.div
//               initial={{ opacity: 0, y: 30 }}
//               whileInView={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.5 }}
//               viewport={{ once: true }}
//             >
//               <h3 className="text-xl font-semibold">{project.title}</h3>
//               <p className="text-gray-400 mt-2">{project.description}</p>
//               <a
//                 href={project.link}
//                 className="mt-4 inline-flex items-center text-purple-400 hover:text-white transition duration-300"
//               >
//                 View Project <ExternalLink className="ml-2 w-4 h-4" />
//               </a>
//             </motion.div>
//           </VerticalTimelineElement>
//         ))}
//       </VerticalTimeline>
//     </section>
//   );
// };

// export default ProjectsTimeline;